// File: src/context/AuthContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import api from '../api/axios';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

const isAndroid = Capacitor.isNativePlatform();

export const AuthProvider = ({ children }) => {

    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const getDashboardPath = (roles) => {
        if (!roles || !Array.isArray(roles)) return '/login';
        if (roles.includes('ROLE_SUPER_ADMIN'))     return '/superadmin/dashboard';
        if (roles.includes('ROLE_OWNER'))            return '/owner/dashboard';
        if (roles.includes('ROLE_HR'))               return '/hr/dashboard';
        if (roles.includes('ROLE_MANAGER'))          return '/employee/dashboard';
        if (roles.includes('ROLE_APPROVAL_MANAGER')) return '/approval-manager/dashboard';
        if (roles.includes('ROLE_EMPLOYEE'))         return '/employee/dashboard';
        return '/login';
    };

    /* ================= INITIAL LOAD ================= */
    useEffect(() => {

        // On Android APK — always clear and require fresh login
        // This fixes the "reinstall still logged in" issue
        if (isAndroid) {
            localStorage.clear();
            setLoading(false);
            return;
        }

        // Website — restore session from localStorage as before
        const storedUser = localStorage.getItem('user');
        const token = localStorage.getItem('token');

        if (storedUser && token) {
            try {
                const parsedUser = JSON.parse(storedUser);
                setUser(parsedUser);

                if (window.location.pathname === '/' || window.location.pathname === '/login') {
                    navigate(getDashboardPath(parsedUser.roles), { replace: true });
                }
            } catch (e) {
                console.error('Error parsing stored user:', e);
                localStorage.clear();
            }
        }

        setLoading(false);
    }, []); // eslint-disable-line

    /* ================= LOGIN ================= */
    const login = async (username, password, companyId) => {

        const payload = {
            username,
            password,
            companyId: companyId ? Number(companyId) : null
        };

        const response = await api.post('/auth/signin', payload);
        const data = response.data;

        const token =
            data.token ||
            data.jwt ||
            data.accessToken ||
            data.access_token;

        if (!token) throw new Error('Token missing from response');

        const rawRoles =
            data.roles ||
            data.authorities ||
            data.authority ||
            [];

        const userRoles = Array.isArray(rawRoles)
            ? rawRoles.map(r =>
                typeof r === 'string'
                    ? r
                    : (r.authority || r.role || r.name || '')
            ).filter(Boolean)
            : [];

        const authUser = {
            id: data.id ?? null,
            username: data.username ?? username,
            roles: userRoles,
            employeeId: data.employeeId ?? null,
            branchId: data.branchId ?? null,
        };

        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(authUser));
        setUser(authUser);

        // On Android — don't navigate (MobileApp handles everything via tabs)
        if (!isAndroid) {
            navigate(getDashboardPath(userRoles), { replace: true });
        }

        return authUser;
    };

    /* ================= LOGOUT ================= */
    const logout = () => {
        setUser(null);
        localStorage.clear();
        if (!isAndroid) {
            navigate('/login', { replace: true });
        }
    };

    /* ================= ROLE CHECK ================= */
    const hasRole = (requiredRoles) => {
        if (!user || !user.roles) return false;
        return requiredRoles.some(r => user.roles.includes(r));
    };

    const value = { user, loading, login, logout, hasRole };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};